"use client";

import Modal from "~/components/v3/themed/Modal";
import { Button } from "~/components/v4/themed/Button";
import { useChat } from "@ai-sdk/react";
import AIMessageParser, { ParsedToolInovcation } from "./AIMessageParser";
import { useEffect, useRef, useState } from "react";
import { AIOutput } from "./AIOutput";
import { ezlog } from "~/utils/ezlog";
import { useRouter } from "next/navigation";

type Props = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
};

const CreateEventWithAIModal = ({ open, onOpenChange }: Props) => {
    const router = useRouter();
    const { messages, input, setInput, handleInputChange, handleSubmit } =
        useChat({
            onToolCall: async ({ toolCall }) => {
                if (toolCall.toolName === "createEvent") {
                    setIsCreatingEvent(true);
                }
            },
            maxSteps: 1,
        });

    const [isCreatingEvent, setIsCreatingEvent] = useState(false);
    const [toolInvocations, setToolInvocations] = useState<
        ParsedToolInovcation[]
    >([]);

    useEffect(() => {
        // if in development mode, set the input to the default value
        if (process.env.NODE_ENV === "development") {
            setInput(
                "Create an event for may 16th 2025 at 10am at a Coffee Shop in San Francisco",
            );
        }
    }, []);

    const parser = new AIMessageParser();
    useEffect(() => {
        const toolInvocations = parser.parseToolInvocations(messages);
        setToolInvocations(toolInvocations);
        console.log("messages", messages, "toolInvocations", toolInvocations);

        // if we detect a completed tool invocation of createEvent
        // then we can set isCreatingEvent to false
        // and navigate to /beta/events/{eventSlug} from the tool invocation result
        if (toolInvocations.length > 0) {
            const lastToolInvocation =
                toolInvocations[toolInvocations.length - 1];
            if (!lastToolInvocation) {
                console.error("lastToolInvocation", lastToolInvocation);
                return;
            }
            if (
                lastToolInvocation.toolName === "createEvent" &&
                lastToolInvocation.result.success
            ) {
                router.push(
                    `/beta/events/${lastToolInvocation.result.eventSlug}`,
                );
            }
        }
    }, [messages]);

    const messagesEndRef = useRef<HTMLDivElement>(null);
    // Auto scroll to bottom whenever messages are updated
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e as unknown as React.FormEvent<HTMLFormElement>);
        }
    };

    return (
        <Modal
            title="Create Event with AI"
            open={open}
            onOpenChange={onOpenChange}
            className="!max-w-[400px]"
            footer={
                <div className="flex w-full justify-end gap-3 border-t border-v3-N-400/50 pt-4">
                    <Button
                        variant="secondary"
                        onClick={() => onOpenChange(false)}
                    >
                        Nevermind
                    </Button>
                </div>
            }
        >
            {/* Messages container with fixed height and scrollable */}
            <div className="flex h-[400px] max-h-[400px] flex-col overflow-y-scroll pb-6">
                <AIOutput
                    messages={messages}
                    input={input}
                    toolInvocations={toolInvocations}
                    isCreatingEvent={isCreatingEvent}
                />
                {/* Empty div for scroll target */}
                <div ref={messagesEndRef} />
            </div>

            {/* Fixed footer with textarea - positioned relative to the left column */}
            <div className="absolute bottom-10 left-0 w-full p-4">
                <div className="mx-auto flex justify-center">
                    <form onSubmit={handleSubmit} className="w-full max-w-md">
                        <textarea
                            className="w-full rounded border border-zinc-300 p-2 shadow-xl dark:border-zinc-800 dark:bg-zinc-900"
                            value={input}
                            placeholder="Say something..."
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            rows={3}
                        />
                    </form>
                </div>
            </div>
        </Modal>
    );
};

export default CreateEventWithAIModal;
